import { UserConfig, AuthData, LoginResponse } from '.././../documentation/User';
import { isEmpty } from 'lodash';
import { apiManagerRequest } from '../../axios';
import setAuthorizationToken from '../../utils/setAuthorizationToken';
import { urlAllUserPermissions, urlUserConfig } from '../urls';
import { AUTH, SERVER } from '../../constants/siteConstants';
import getCommonLoginRequest from '../../utils/getCommonLoginRequest';
import Session from '../../utils/session';
import Dates from '../../utils/dates';

export const getUserPermissions = async (idUsuario) => {
  try {
    let permissionUrl = `${urlAllUserPermissions}?idUser=${idUsuario}&isAdmin=false`;

    const { data: permissions } = await apiManagerRequest.get(permissionUrl);
    return permissions;
  } catch (e) {
    throw new Error(e.message);
  }
};

/**
 * @returns {Promise<UserConfig>}
 */
const getUserConfig = async (body) => {
  try {
    const { data: userConfig } = await apiManagerRequest.post(urlUserConfig, body);

    return userConfig;
  } catch (error) {
    throw new Error(e.message);
  }
};

export const setUserDetailInStorage = (userDetail) => {
  Session.updateAuth(userDetail);
};

export const setUserInStorage = (
  userDetail,
  token,
  exp,
  userPermissions,
  userHasAcceptedTerms,
  userHasDOC24Permission,
  userSurveyResponse,
  isCircle,
  managementUnit,
  hospitalizationInAffiliateValidation,
) => {
  Session.remove();
  Session.save(
    userDetail,
    token,
    exp,
    userPermissions,
    userHasAcceptedTerms,
    userHasDOC24Permission,
    userSurveyResponse,
    isCircle,
    managementUnit,
    hospitalizationInAffiliateValidation,
  );
};

/**
 * @param {LoginResponse} loginData
 * @returns {Promise<{ authData: AuthData }>}
 */
export const getUserAuthData = async (loginData) => {
  setAuthorizationToken(loginData.token);
  let response = {};

  try {
    const userConfig = await getUserConfig(loginData?.modelEspecifico);

    userConfig.userDetail.modelEspecifico = loginData.modelEspecifico;

    userConfig.userPermissions = {
      ...userConfig.userPermissions,
      leftNavigationAndMenuButtons: [
        ...userConfig.userPermissions.leftNavigationAndMenuButtons,
        ...(userConfig.medicalHistoryEnable
          ? [
              {
                'id': 'atencion-medica',
                'active': true,
                '@_habilitado': true,
              },
            ]
          : []),
        ...(userConfig.userHasDOC24Permission
          ? [
              {
                'id': 'doc-24',
                'active': true,
                '@_habilitado': true,
              },
            ]
          : []),
      ],
    };

    response = {
      statusCode: 200,
      authData: {
        ...userConfig,
        token: loginData.token,
        tokenExpirationDate: Dates.get(loginData.exp, 'YYYYMMDDHHmmss').toDate(),
      },
    };
  } catch (error) {
    throw new Error(error.message);
  }

  return response;
};

export const loginUserRequest = (url, params) => async () => {
  const jsonBody = {
    email: params.email,
    password: params.password,
    ...getCommonLoginRequest(),
  };
  try {
    /**
     * @type {{data: LoginResponse}}
     */
    const loginResponse = await apiManagerRequest.post(url, jsonBody);
    if (loginResponse?.data && loginResponse.data?.token && !isEmpty(loginResponse.data.token)) {
      try {
        const { data: loginData } = loginResponse;
        const userDataResponse = await getUserAuthData(loginData);
        const {
          userDetail,
          userPermissions,
          token,
          tokenExpirationDate,
          userHasDOC24Permission,
          userSurveyResponse,
          userHasAcceptedTerms,
          isCircle,
          managementUnit,
          hospitalizationInAffiliateValidation,
        } = userDataResponse.authData;

        setUserInStorage(
          userDetail,
          token,
          tokenExpirationDate,
          userPermissions,
          userHasAcceptedTerms,
          userHasDOC24Permission,
          userSurveyResponse,
          isCircle,
          managementUnit,
          hospitalizationInAffiliateValidation,
        );

        return userDataResponse;
      } catch (error) {
        return {
          statusCode: 401,
          error: true,
          message: error.message,
          errorDescription: error,
        };
      }
    } else {
      // Reviso el codigo de error
      errorCode = loginResponse.data.errNumber;
      if (errorCode == 114) {
        return {
          statusCode: 500,
          error: true,
          message: AUTH[LOGIN_UNAUTHORIZED],
        };
      } else {
        return {
          statusCode: 500,
          error: true,
          message: SERVER[500],
        };
      }
    }
  } catch (e) {
    return {
      statusCode: 403,
      error: true,
      message: AUTH.LOGIN_UNAUTHORIZED,
      errorDescription: e,
    };
  }
};

export const renewTokenRequest = (url, params) => async () => {
  const jsonBody = {
    token: params.token,
    ...getCommonLoginRequest(),
  };
  try {
    const renewTokenResponse = await apiManagerRequest.post(url, jsonBody);
    if (
      renewTokenResponse?.data &&
      renewTokenResponse.data.token &&
      !isEmpty(renewTokenResponse.data.token)
    ) {
      const { exp: newExp, token: newToken } = renewTokenResponse.data;
      Session.updateAuthenticationToken(newToken, Dates.get(newExp, 'YYYYMMDDHHmmss').toDate());
      setAuthorizationToken(newToken);
      return {
        statusCode: 200,
        newExp,
        newToken,
      };
    }

    return {
      statusCode: renewTokenResponse.status,
      error: true,
      message: AUTH.RENEW_TOKEN_ERROR,
      errorDescription: e,
    };
  } catch (e) {
    return {
      statusCode: e?.response?.status,
      error: true,
      message: AUTH.RENEW_TOKEN_ERROR,
      errorDescription: e,
    };
  }
};
